import { FC, useRef, useState, KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

import Button, { ButtonSize, ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import IconButton from '@zvuk-b2b/react-uikit/ui/IconButton';
import Radio from '@zvuk-b2b/react-uikit/ui/Radio';
import IconChevronDown from '@zvuk-b2b/react-uikit/ui/icons/ChevronDown';
import IconChevronUp from '@zvuk-b2b/react-uikit/ui/icons/ChevronUp';
import IconX from '@zvuk-b2b/react-uikit/ui/icons/X';
import Dropdown, { DropdownItemDataType } from '@zvuk-b2b/react-uikit/ui/Dropdown';
import ToolkitUtils from '@zvuk-b2b/react-uikit/toolkit/utils';
import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';

import { usePlayerContext } from 'application/providers/PlayerProvider';
import { PlayButton } from 'application/components/PlayButton';
import MediaListItemProgressSlider from 'application/components/MediaList/MediaListItem/MediaListItemProgressSlider';
import FileUtils from 'application/utils/FileUtils';
import { PromoRollSynthVoiceType } from '@entities/PromoRollSynthJob/model';

import { TEXTS } from '../PromoRollSynthModal/texts';
import styles from './VoiceSelect.module.scss';

type Props = {
  value: string;
  options: PromoRollSynthVoiceType[];
  onChange: (id: string) => void;
};

const SAMPLE_ID_PREFIX = 'voice_sample';

const VoiceSelect: FC<Props> = ({ value, options, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { play, pause, resume, isPaused, isLoading, fileUuid, progress, changeProgress } = usePlayerContext();
  const { isMobile } = useMedia();

  const selectedOption = options.find((v) => v.voiceId === value);

  const handleSampleClick = (voice: PromoRollSynthVoiceType) => {
    const sampleId = `${SAMPLE_ID_PREFIX}_${voice.id}`;

    if (fileUuid === sampleId && !isPaused) {
      pause();
    } else if (fileUuid === sampleId && isPaused) {
      resume();
    } else {
      play({ fileUuid: sampleId, fileName: voice.title, fileUrl: FileUtils.generateMediafileURL(voice.fileUrl) });
    }
  };

  const handleSelect = (voice: PromoRollSynthVoiceType) => {
    onChange(voice.voiceId);
    setIsOpen(false);
  };

  const handleSampleKeyDown = (e: KeyboardEvent, voice: PromoRollSynthVoiceType) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.stopPropagation();
      handleSampleClick(voice);
    }
  };

  const items: DropdownItemDataType[] = options.map((voice) => {
    const sampleId = `${SAMPLE_ID_PREFIX}_${voice.id}`;
    const isSampleActive = fileUuid === sampleId;
    const isPlaying = isSampleActive && !isPaused;

    return {
      key: voice.id,
      value: voice.title,
      selected: value === voice.voiceId,
      onClick: () => handleSelect(voice),
      content: (
        <>
          <Radio
            elementId={`voice_radio_${voice.id}`}
            isChecked={value === voice.voiceId}
            onChange={() => handleSelect(voice)}
          />

          <div
            role="button"
            tabIndex={0}
            data-testid={`voice_sample_${voice.id}`}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              handleSampleClick(voice);
            }}
            onKeyDown={(e) => handleSampleKeyDown(e, voice)}>
            <PlayButton
              isMediafilePlaying={isPlaying}
              isMediaFilePaused={isSampleActive && isPaused}
              isLoading={isSampleActive && isLoading}
            />
          </div>

          <span className={styles.optionLabel}>{voice.title}</span>

          {isSampleActive && (
            <MediaListItemProgressSlider
              elementId={`voice_progress_${voice.id}`}
              progress={progress}
              onChange={changeProgress}
            />
          )}
        </>
      ),
    };
  });

  return (
    <div
      className={styles.container}
      ref={containerRef}>
      <span className={styles.label}>{TEXTS.FIELD_VOICE_LABEL}</span>

      <Button
        ref={triggerRef}
        elementId="voice_select_trigger"
        type={ButtonType.SECONDARY}
        size={ButtonSize.MEDIUM}
        className={classNames(styles.trigger, { [styles.triggerOpen]: isOpen })}
        onClick={() => setIsOpen((v) => !v)}>
        <span className={classNames({ [styles.placeholder]: !selectedOption })}>
          {selectedOption?.title ?? TEXTS.FIELD_VOICE_PLACEHOLDER}
        </span>
        {isOpen ? <IconChevronUp /> : <IconChevronDown />}
      </Button>

      {isOpen &&
        (() => {
          const dropdown = (
            <>
              {isMobile && (
                <IconButton
                  elementId="voice_select_dropdown_close_button"
                  className={styles.dropdownCloseButton}
                  onClick={(event) => {
                    setIsOpen(false);
                    event.preventDefault();
                  }}>
                  <IconX />
                </IconButton>
              )}

              <Dropdown
                elementId="voice_select_dropdown"
                className={styles.dropdown}
                itemContentClassName={styles.optionContent}
                items={items}
                anchorNode={triggerRef.current!}
                parentNode={containerRef.current!}
                style={isMobile ? undefined : { width: ToolkitUtils.getWidth(triggerRef.current!) }}
                onClose={() => setIsOpen(false)}
                isFullMode={isMobile}
                withoutWrapper={isMobile}
                isNeedCloseWhileParentScrolling={!isMobile}
              />
            </>
          );

          return isMobile ? createPortal(dropdown, document.body) : dropdown;
        })()}
    </div>
  );
};

export default VoiceSelect;
